<script lang="ts" setup>
import {ClockIcon} from "@heroicons/vue/24/outline";
import {ArrowsRightLeftIcon, CalendarDaysIcon, ChatBubbleLeftEllipsisIcon, UserIcon} from "@heroicons/vue/24/solid";
import {computed, defineAsyncComponent, onMounted, ref} from "vue"
import {useChatSessionStore} from "@src/stores/chatSession";
import ScheduleMessagesService from '@src/services/scheduleMessages'
import type ScheduleMessage from '@src/types/ScheduleMessage'

const ModalScheduleEdit = defineAsyncComponent(() => import("@src/components/shared/modals/ScheduleMessageModal/ScheduleMessage.vue"));
import {shorten} from "@src/helpers/utils";
import {Campaign} from "@src/types/Campaign";
import useStore from "@src/stores/store";
import {ScheduledWaapiCampaign} from "@src/types/ScheduledWaapiCampaign";
const WaapiCampaign = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/WaapiCampaign.vue"));
import {useWAAPIStore} from "@src/stores/waapi";

const loading = ref<boolean>(false);

const canScheduleMessage = ref<boolean>(false);

const props = defineProps<{
    session?: any | null
}>()

const emit = defineEmits(['closeModal'])

const sessionStore = useChatSessionStore()
const waapiStore = useWAAPIStore()
const store = useStore()

const selectedSession = computed(() => {
    return props.session ?? sessionStore.selectedSession
})

const costumerToken = computed(() => {
    return selectedSession.value?.costumer?.token ?? null
})

const costumerName = computed(() => {
    return selectedSession.value?.costumer?.name ?? 'Cliente'
})

const sessionId = computed(() => {
    return selectedSession.value?.id ?? null
})

const scheduleMessages = ref<ScheduleMessage[]>([])
const scheduledCampaigns = ref<Campaign[]>([])
const openModalScheduleEdit = ref<boolean>(false);
const selectedMessage = ref<any>(null)
const campaignForm = ref<ScheduledWaapiCampaign>()

const loadMessages = async () => {
    if (!costumerToken.value) {
        scheduleMessages.value = []
        scheduledCampaigns.value = []
        canScheduleMessage.value = false
        return
    }

    loading.value = true

    try {
        const response = await ScheduleMessagesService.getByToken(costumerToken.value)

        scheduledCampaigns.value = response.campaigns
        scheduleMessages.value = response.data
        canScheduleMessage.value = response.canScheduleMessage
    } catch (e) {
        console.error(e)
        scheduleMessages.value = []
        scheduledCampaigns.value = []
    } finally {
        loading.value = false
    }
}

const selectMessage = (message: any) => {
    message.no_token = false
    selectedMessage.value = message
    openModalScheduleEdit.value = true
}

const newMessage = () => {
    selectedMessage.value = {
        id: -1,
        channel_id: null,
        transferred_operator_id: null,
        scheduled_at: null,
        message: '',
        no_token: false
    }

    openModalScheduleEdit.value = true
}

onMounted(async () => {
    await loadMessages()
})

const setCampaignForm = (form: ScheduledWaapiCampaign) => {
    openModalScheduleEdit.value = false
    campaignForm.value = form
    waapiStore.showCampaignModal = true
    setTimeout(() => {
        emit('closeModal');
    }, 500);
}
const handleSaved = async () => {
    openModalScheduleEdit.value = false

    await loadMessages()

    emit('closeModal')
}
</script>

<template>
    <div class="fixed z-10 inset-0 overflow-y-auto" role="dialog" @click.stop>
        <div
            class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 backdrop-blur-[2px]">
            <div class="fixed inset-0 opacity-25 bg-gray-500 dark:bg-gray-400 transition-opacity"
                 @click="$emit('closeModal')"></div>
            <div
                class="inline-block z-20 bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-3xl">
                <div class="p-4 min-w-120 max-w-160  bg-white dark:bg-gray-800 shadow-2xl dark:shadow-gray-800">
                    <div class="sm:flex sm:items-start">
                        <div class="mt-3 text-center sm:mt-0 w-full sm:text-left">
                            <div class="flex flex-row mb-6">
                                <ClockIcon class="w-6 h-6 mr-3 text-gray-900 dark:text-gray-100"/>
                                <h3 class="text-lg leading-6 text-gray-900 dark:text-gray-100">
                                    Mensagens Agendadas -
                                    {{ shorten(costumerName, 28) }}
                                </h3>
                            </div>
                            <div style="max-height: 320px; overflow-y: auto">
                                <div v-for="message in scheduleMessages" :key="message.id"
                                     class="flex w-full justify-between border-b p-2 pb-4 mt-3">
                                    <div class="w-full">
                                        <div class="w-full flex justify-between">
                                            <div class="flex items-center">
                                                <UserIcon class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                                <h4 class="heading-2 text-black/70 dark:text-white/70">
                                                    {{ message.user_name }}
                                                </h4>
                                            </div>
                                            <div class="flex items-center">
                                                <CalendarDaysIcon
                                                    class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                                <h4 class="heading-2 text-black/70 dark:text-white/70">
                                                    {{ new Date(message.scheduled_at).toLocaleString('pt-BR') }}
                                                </h4>
                                            </div>
                                        </div>
                                        <div class="flex items-center mt-4 items-start">
                                            <ChatBubbleLeftEllipsisIcon
                                                class="min-w-5 h-5 mr-3 text-green-500 dark:text-gray-100"/>
                                            <h4 class="heading-2 text-black/70 dark:text-white/70 font-normal text-start">
                                                {{ message.message }}
                                            </h4>
                                        </div>
                                        <div class="flex items-center mt-3 items-center">
                                            <ArrowsRightLeftIcon
                                                class="min-w-5 h-5 mr-3 text-red-600 dark:text-red-400"/>
                                            <h4 v-if="message.transferred_operator_name"
                                                class="heading-2 text-black/70 dark:text-white/70 font-normal italic">
                                                Após o envio, transferir para <span
                                                class="font-bold"> {{ message.transferred_operator_name }}</span>
                                            </h4>
                                            <h4 v-else
                                                class="heading-2 text-black/70 dark:text-white/70 font-normal italic">
                                                Após o envio, não transfere o atendimento
                                            </h4>
                                        </div>

                                    </div>

                                    <div v-if="canScheduleMessage" class="mt-2 ml-4">
                                        <Button
                                            class="flex justify-center rounded-md py-3 px-4 bg-brand-400 text-gray-100 font-semibold cursor-pointer hover:bg-brand-500"
                                            @click="selectMessage(message)">
                                            Editar
                                        </Button>
                                    </div>
                                </div>
                                <div v-for="campaign in scheduledCampaigns" :key="campaign.id"
                                     class="flex w-full justify-between border-b p-2 pb-4 mt-3"
                                >
                                    <div class="w-full">
                                        <div class="w-full flex justify-between">
                                            <div class="flex items-center">
                                                <UserIcon class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                                <h4 class="heading-2 text-black/70 dark:text-white/70">
                                                    {{ campaign.user_name }}
                                                </h4>
                                            </div>
                                            <div class="flex">
                                                <CalendarDaysIcon
                                                    class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                                <h4 class="mr-4 heading-2 text-black/70 dark:text-white/70">
                                                    {{ new Date(campaign.scheduled_at).toLocaleString('pt-BR') }}
                                                </h4>
                                                <div v-if="canScheduleMessage">
                                                    <Button
                                                        class="flex justify-center rounded-md py-3 px-4 bg-brand-400 text-gray-100 font-semibold cursor-pointer hover:bg-brand-500"
                                                        @click="selectMessage({
                                                    id: campaign.id,
                                                    channel_id: campaign.channel_id,
                                                    transferred_operator_id: campaign.transferred_operator_id,
                                                    scheduled_at: campaign.scheduled_at,
                                                    message: campaign.model?.name,
                                                })">
                                                        Editar
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex items-start mt-4">
                                            <img v-if="!store.settings.darkMode"
                                                 class="w-6 h-6 mr-3"
                                                 src="@src/assets/vectors/official_icon.svg"/>
                                            <img v-else class="w-6 h-6 mr-3"
                                                 src="@src/assets/vectors/official_icon_white.svg"/>
                                            <h4 class="heading-2 text-black/70 font-normal text-start bg-black/10 p-3 px-4 rounded-sm dark:text-[#FAFAFA] dark:bg-black/20 line-clamp-5 overflow-y-auto message-scroll">
                                                {{ campaign.model?.name }}
                                            </h4>
                                        </div>
                                        <div class="flex items-center mt-3 items-center">
                                            <ArrowsRightLeftIcon
                                                class="min-w-5 h-5 mr-3 text-red-600 dark:text-red-400"/>
                                            <h4 v-if="(campaign.transferred_operator_id ?? 0) > 0"
                                                class="heading-2 text-black/70 dark:text-white/70 font-normal italic">
                                                Após o envio, transferir para <span
                                                class="font-bold"> {{ campaign.transferred_operator_name }}</span>
                                            </h4>
                                            <h4 v-else
                                                class="heading-2 text-black/70 dark:text-white/70 font-normal italic">
                                                Após o envio, não transfere o atendimento
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bg-gray-50 dark:bg-gray-700 px-4 py-6 flex justify-between">
                    <button
                        class="text-sm justify-center cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-3 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-500"
                        type="button"
                        @click="$emit('closeModal')"
                    >
                        Fechar
                    </button>

                    <button
                        v-if="canScheduleMessage"
                        class="disabled:opacity-25 cursor-pointer disabled:cursor-auto text-sm justify-center rounded-md px-4 py-3 bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                        type="button"
                        @click="newMessage()"
                    >
                        Adicionar
                    </button>
                </div>
            </div>
        </div>

        <Teleport to="#modal-root">
            <Transition
                v-if="openModalScheduleEdit"
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2">
                <ModalScheduleEdit
                    :costumer_name="costumerName"
                    :costumer_token="costumerToken"
                    :message="selectedMessage"
                    :no_token="false"
                    :session_id="sessionId"
                    @closeModal="openModalScheduleEdit = false"
                    @saved="handleSaved"
                    @scheduleCampaignForm="setCampaignForm"
                />
            </Transition>
            <Transition>
                <WaapiCampaign
                    v-if="waapiStore.showCampaignModal"
                    :costumer-token="costumerToken"
                    :form="campaignForm"
                />
            </Transition>
        </teleport>
    </div>

</template>

<style scoped>
</style>